import React, { useState, useEffect } from 'react';
import { toast } from 'sonner';
import api from '../services/api';
import { Card, CardHeader, CardTitle, CardContent } from '../components/Card';
import Button from '../components/Button';
import { Users, FileText, Shield, CheckCircle, XCircle, UserPlus, Wallet } from 'lucide-react';
import { formatDate } from '../lib/utils';

export default function AdminPanel() {
  const [users, setUsers] = useState([]);
  const [projects, setProjects] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('users');
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [newUser, setNewUser] = useState({
    Email: '',
    Password: '',
    Full_Name: '',
    Company_Name: '',
    Role: 'USER'
  });

  useEffect(() => {
    fetchData();
  }, [activeTab]);

  const fetchData = async () => {
    setIsLoading(true);
    try {
      if (activeTab === 'users') {
        const response = await api.get('/users');
        setUsers(response.data || []);
      } else {
        const response = await api.get('/control_panel', { params: { limit: 100 } });
        setProjects(response.data.data || []);
      }
    } catch (error) {
      toast.error('Ошибка загрузки данных');
    } finally {
      setIsLoading(false);
    }
  };

  const toggleUserStatus = async (userId, currentStatus) => {
    try {
      await api.patch(`/users/${userId}`, { Is_Active: !currentStatus });
      toast.success('Статус пользователя обновлен');
      fetchData();
    } catch (error) {
      toast.error('Ошибка обновления статуса');
    }
  };

  const changeUserRole = async (userId, newRole) => {
    try {
      await api.patch(`/users/${userId}`, { Role: newRole });
      toast.success('Роль пользователя обновлена');
      fetchData();
    } catch (error) {
      toast.error('Ошибка обновления роли');
    }
  };

  const createUser = async (e) => {
    e.preventDefault();
    try {
      await api.post('/users', newUser);
      toast.success('Пользователь создан');
      setShowCreateModal(false);
      setNewUser({ Email: '', Password: '', Full_Name: '', Company_Name: '', Role: 'USER' });
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.error || 'Ошибка создания пользователя');
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h1 className="text-3xl font-bold">Панель администратора</h1>
        {activeTab === 'users' && (
          <Button onClick={() => setShowCreateModal(true)}>
            <UserPlus className="h-4 w-4 mr-2" />
            Создать пользователя
          </Button>
        )}
      </div>

      <div className="flex space-x-2 border-b">
        <button
          onClick={() => setActiveTab('users')}
          className={`flex items-center space-x-2 px-4 py-2 border-b-2 transition-colors ${
            activeTab === 'users'
              ? 'border-primary text-primary'
              : 'border-transparent text-muted-foreground hover:text-foreground'
          }`}
        >
          <Users className="h-4 w-4" />
          <span>Пользователи</span>
        </button>
        <button
          onClick={() => setActiveTab('projects')}
          className={`flex items-center space-x-2 px-4 py-2 border-b-2 transition-colors ${
            activeTab === 'projects'
              ? 'border-primary text-primary'
              : 'border-transparent text-muted-foreground hover:text-foreground'
          }`}
        >
          <FileText className="h-4 w-4" />
          <span>Все проекты</span>
        </button>
      </div>

      {isLoading ? (
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
        </div>
      ) : activeTab === 'users' ? (
        <Card>
          <CardHeader>
            <CardTitle>Управление пользователями</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b">
                    <th className="text-left py-3 px-4">Email</th>
                    <th className="text-left py-3 px-4">Имя</th>
                    <th className="text-left py-3 px-4">Компания</th>
                    <th className="text-left py-3 px-4">Баланс</th>
                    <th className="text-left py-3 px-4">Роль</th>
                    <th className="text-left py-3 px-4">Статус</th>
                    <th className="text-left py-3 px-4">Создан</th>
                    <th className="text-left py-3 px-4">Действия</th>
                  </tr>
                </thead>
                <tbody>
                  {users.map(user => (
                    <tr key={user.ID} className="border-b hover:bg-muted/50">
                      <td className="py-3 px-4">{user.Email}</td>
                      <td className="py-3 px-4">{user.Full_Name}</td>
                      <td className="py-3 px-4 text-sm text-muted-foreground">{user.Company_Name || '-'}</td>
                      <td className="py-3 px-4">
                        <a 
                          href="/admin/billing" 
                          className="flex items-center text-primary hover:underline"
                          onClick={(e) => {
                            e.preventDefault();
                            window.location.href = '/admin/billing';
                          }}
                        >
                          <Wallet className="h-4 w-4 mr-1" />
                          Управление
                        </a>
                      </td>
                      <td className="py-3 px-4">
                        <select
                          value={user.Role}
                          onChange={(e) => changeUserRole(user.ID, e.target.value)}
                          className="px-2 py-1 border rounded text-sm"
                        >
                          <option value="USER">User</option>
                          <option value="ADMIN">Admin</option>
                        </select>
                      </td>
                      <td className="py-3 px-4">
                        {user.Is_Active ? (
                          <span className="inline-flex items-center text-green-600">
                            <CheckCircle className="h-4 w-4 mr-1" />
                            Активен
                          </span>
                        ) : (
                          <span className="inline-flex items-center text-red-600">
                            <XCircle className="h-4 w-4 mr-1" />
                            Заблокирован
                          </span>
                        )}
                      </td>
                      <td className="py-3 px-4 text-sm text-muted-foreground">
                        {formatDate(user.Created_At)}
                      </td>
                      <td className="py-3 px-4">
                        <Button
                          size="sm"
                          variant={user.Is_Active ? 'destructive' : 'outline'}
                          onClick={() => toggleUserStatus(user.ID, user.Is_Active)}
                        >
                          {user.Is_Active ? 'Заблокировать' : 'Разблокировать'}
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
      ) : (
        <Card>
          <CardHeader>
            <CardTitle>Все проекты</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b">
                    <th className="text-left py-3 px-4">Название</th>
                    <th className="text-left py-3 px-4">Владелец</th>
                    <th className="text-left py-3 px-4">Email владельца</th>
                    <th className="text-left py-3 px-4">Создан</th>
                    <th className="text-left py-3 px-4">Обновлен</th>
                  </tr>
                </thead>
                <tbody>
                  {projects.map(project => (
                    <tr key={project.ID} className="border-b hover:bg-muted/50">
                      <td className="py-3 px-4 font-medium">{project.Script_Name}</td>
                      <td className="py-3 px-4">{project.User?.Full_Name || 'N/A'}</td>
                      <td className="py-3 px-4">{project.User?.Email || 'N/A'}</td>
                      <td className="py-3 px-4 text-sm text-muted-foreground">
                        {formatDate(project.Created_At)}
                      </td>
                      <td className="py-3 px-4 text-sm text-muted-foreground">
                        {formatDate(project.Updated_At)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Modal создания пользователя */}
      {showCreateModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-md">
            <h2 className="text-2xl font-bold mb-4">Создать пользователя</h2>
            <form onSubmit={createUser} className="space-y-4">
              <div>
                <label className="block text-sm font-medium mb-1">Email *</label>
                <input
                  type="email"
                  required
                  value={newUser.Email}
                  onChange={(e) => setNewUser({ ...newUser, Email: e.target.value })}
                  className="w-full px-3 py-2 border rounded-md"
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Пароль *</label>
                <input
                  type="password"
                  required
                  minLength={6}
                  value={newUser.Password}
                  onChange={(e) => setNewUser({ ...newUser, Password: e.target.value })}
                  className="w-full px-3 py-2 border rounded-md"
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Полное имя *</label>
                <input
                  type="text"
                  required
                  value={newUser.Full_Name}
                  onChange={(e) => setNewUser({ ...newUser, Full_Name: e.target.value })}
                  className="w-full px-3 py-2 border rounded-md"
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Название компании</label>
                <input
                  type="text"
                  value={newUser.Company_Name}
                  onChange={(e) => setNewUser({ ...newUser, Company_Name: e.target.value })}
                  className="w-full px-3 py-2 border rounded-md"
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Роль</label>
                <select
                  value={newUser.Role}
                  onChange={(e) => setNewUser({ ...newUser, Role: e.target.value })}
                  className="w-full px-3 py-2 border rounded-md"
                >
                  <option value="USER">User</option>
                  <option value="ADMIN">Admin</option>
                </select>
              </div>
              <div className="flex space-x-2 pt-4">
                <Button type="submit" className="flex-1">Создать</Button>
                <Button 
                  type="button" 
                  variant="outline" 
                  onClick={() => {
                    setShowCreateModal(false);
                    setNewUser({ Email: '', Password: '', Full_Name: '', Company_Name: '', Role: 'USER' });
                  }}
                  className="flex-1"
                >
                  Отмена
                </Button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
